import { GetServerSideProps } from "next";
import { useState } from "react";
import Ad from "src/components/ads/Ad";
import StickyBottomAd from "src/components/ads/StickyBottomAd";
import Button from "src/components/button/Button";
import ContentWrapper from "src/components/contentwrapper/ContentWrapper";
import AdUnitWithPosition from "src/components/forsiden/AdUnitWithPosition";
import InfoBox from "src/components/infobox/InfoBox";
import MoviesByMonth from "src/components/kommendefilmer/MoviesByMonth";
import PremieresThisWeek from "src/components/kommendefilmer/PremieresThisWeek";
import UpcomingMoviesLoader from "src/components/kommendefilmer/UpcomingMoviesLoader";
import KommerSeo from "src/components/seo/KommerSeo";
import PosterListSvg from "src/components/svg/PosterListSvg";
import TextListSvg from "src/components/svg/TextListSvg";
import TopOfPageSponsor from "src/components/takoversponsor/TopOfPageSponsor";
import AdWrapper from "src/lib/contexts/AdContext";
import { LocationWrapper } from "src/lib/contexts/LocationContext";
import { useOverriddenAdunits } from "src/lib/server/ads";
import { setDefaultCacheHeaders } from "src/lib/server/cache";

import { ICommonProps, getServerData } from "src/lib/server/sanity";
import styled, { css } from "src/lib/styles/css";
import { type IFilmomtale } from "src/lib/types/Filmomtale";

const ADPROXIMITY = 0;


//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    // TODO: Preload data!
    setDefaultCacheHeaders(context);

    return {
        props: {
        }
    };


});
//#endregion

//#region [Props]
export type KommerPåKinoProps = {
    movies: IFilmomtale[];
} & ICommonProps;
//#endregion

/**
 * TODO: Denne siden bruker client side fetching fra Filminfo pga at den er avhengig av Location.
 * I fremtiden bør denne bruke server side fethching kombinert med client side fetching.
 * Location er kun brukt for å finne ut hvilke filmer det skal vises kjøpslinker isteder for watchlisting

 */

//#region [Component]
export default function KommerPåKinoPage({ appData, adUnits, hasSponsor, sponsorTopImageDesktop, sponsorTopImageMobile, sponsorClickUrl }: KommerPåKinoProps) {
    const [isPosterList, setIsPosterList] = useState(true);
    const finalAdUnits = useOverriddenAdunits(ADS, adUnits);
    const hasPackshot = finalAdUnits.find(adUnit => adUnit.targetId === "FW_bottomSticky")?.isPackshot ?? false;

    return <section>
        <KommerSeo />
        <AdWrapper ads={finalAdUnits} defaultProximity={ADPROXIMITY}>
            <SContentWrapper>
                <InfoBox messageBanner={appData.settings} />
                <SPageHeader>

                    <SPageTitle>Kommende kinofilmer</SPageTitle>
                    <SViewToggleContainer>
                        <Button
                            onClick={() => setIsPosterList(!isPosterList)}
                            text={isPosterList ? "Tekstliste" : "Posterliste"}
                            icon={isPosterList ? <STextListSvg /> : <SPosterListSvg />} />
                    </SViewToggleContainer>
                    {hasSponsor && <STopOfPageSponsor mobileImages={sponsorTopImageMobile} desktopImages={sponsorTopImageDesktop} clickUrl={sponsorClickUrl} />}
                </SPageHeader>
                <LocationWrapper canUseGeoLocation={appData.featureFlags.isGeolocationAllowed ?? false}>
                    <UpcomingMoviesLoader>
                        <PremieresThisWeek showTextList={!isPosterList} />
                        <MoviesByMonth adKeys={DYNAMIC_AD_KEYS} showTextList={!isPosterList} />
                        {/** Ligger inne i loader slik at den ikke vises før innholdet er lastet */}
                        <Ad targetId="FW_parallax_bunn1_kommende" removeIfNoAd supportsFixedAd height="300" mobileScreenWidth />
                    </UpcomingMoviesLoader>
                </LocationWrapper>
            </SContentWrapper>
            <StickyBottomAd isPackshot={hasPackshot} />
        </AdWrapper>

    </section>;
}
//#endregion

//#region [Styles]
const SContentWrapper = styled(ContentWrapper)`
	margin: 10px var(--page-padding);
	width: calc(100% - var(--page-padding) * 2);
	min-height: 100dvh;

	@media (min-width: 550px) {
		margin: 35px auto;
	}
`

const SPageHeader = styled.header`
	width: 100%;
	display: flex;
    flex-wrap: wrap;
	justify-content: space-between;
    gap: 20px;
	padding-bottom: 30px;
	position: relative;
	align-items: center;
	z-index: 1;

	&:not(:first-child) {
		margin-top: var(--page-padding);
	}

	@media (min-width: 550px) {
		margin-top: 50px;

	}

`;

const STopOfPageSponsor = styled(TopOfPageSponsor)`
    flex: 0 0 100%;

    @media (min-width: 550px) {
        flex: 0 1 auto;
        margin: 0 0 0 auto;
        .topOfPageSponsorInnerContainer {
            margin-bottom: 0;
        }
    }
`;

const SPageTitle = styled.h1`
	font-size: var(--font-bodytext-l_size);
	line-height: 38px;
	color: var(--textcolor);

	@media (min-width: 550px) {
		font-size: var(--font-title-xl_size);
		line-height: 58px;
	}
`;

const SViewToggleContainer = styled.div`
	justify-self: end;
`;

const CSSButtonIcons = css`
	height: 13px !important;
	max-height: 13px !important;
`;

const STextListSvg = styled(TextListSvg)`
	${CSSButtonIcons}
`;

const SPosterListSvg = styled(PosterListSvg)`
	${CSSButtonIcons}
`;
//#endregion


const [ADS, DYNAMIC_AD_KEYS] = (() => {
    const dynamicAdKeys = [
        "FW_parallax_topp_kommende",
        "FW_parallax_midt1_kommende",
        "FW_parallax_midt2_kommende",
        "FW_parallax_midt3_kommende"
    ];
    const result: AdUnitWithPosition[] = [
        { auId: '0000000000077209', auW: '2000', auH: '600', targetId: 'FW_parallax_topp_kommende' },
        //{ auId: '3880e3', auW: '2000', auH: '600', targetId: 'FW_parallax_topp_kommende' }, // SAN-509
        { auId: '000000000007721e', auW: '2000', auH: '600', targetId: 'FW_parallax_midt1_kommende' },
        { auId: '0000000000077223', auW: '2000', auH: '600', targetId: 'FW_parallax_midt2_kommende' },
        { auId: '0000000000077225', auW: '2000', auH: '600', targetId: 'FW_parallax_midt3_kommende' },
        { auId: '3c3cd5', auW: '50vw', targetId: 'FW_bottomSticky', requestMode: "hasTarget" },

        //{ auId: '0000000000085dba', auW: '2000', auH: '600', targetId: 'FW_parallax_bunn2_kommende' } // NOT IN USE (YET)
    ];
    const NUM_REPEATING_ADS = 20;

    for (let i = 0; i < NUM_REPEATING_ADS; i++) {
        result.push({ auId: '0000000000077225', auW: '2000', auH: '600', targetId: `FW_parallax_midt3_kommende_${i}` });
        dynamicAdKeys.push(`FW_parallax_midt3_kommende_${i}`);
    }
    result.push({ auId: '0000000000084149', auW: '2000', auH: '600', targetId: 'FW_parallax_bunn1_kommende' });
    return [result, dynamicAdKeys];
})();